import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Select, Input } from "antd";
import { CalendarX } from "@phosphor-icons/react";
import {
  InboxModalFieldGroup,
  InboxModalHint,
  InboxModalLabel,
  InboxModalErrorBanner,
} from "../AIPane/AIPaneStyled";

/**
 * Dismiss-deadline modal (deadline calendaring).
 *
 * A deadline card can be refused but never silently ignored — the BE
 * requires a reason, and `other` requires a free-text note. Mirrors
 * the AssignTaskModal shape (Modal + Select + optional note).
 *
 * Confirms via `onConfirm({ reason, note })`; the parent owns the
 * dispatch so this stays UI-only.
 */

export const DISMISS_REASON_OPTIONS = [
  { value: "already_calendared", label: "Already calendared" },
  { value: "not_rating_decision", label: "Not actually a decision" },
  { value: "duplicate", label: "Duplicate" },
  { value: "other", label: "Other…" },
];

const DismissDeadlineModal = ({
  open,
  cardTitle = "",
  onClose,
  onConfirm,
  busy = false,
}) => {
  const [reason, setReason] = useState("already_calendared");
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setReason("already_calendared");
    setNote("");
    setError(null);
  }, [open]);

  const handleConfirm = useCallback(() => {
    if (reason === "other" && !note.trim()) {
      setError("Add a short note explaining why.");
      return;
    }
    setError(null);
    onConfirm({ reason, note: note.trim() });
  }, [reason, note, onConfirm]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Dismiss deadline"
      cancelText="Cancel"
      okButtonProps={{
        danger: true,
        disabled: busy || (reason === "other" && !note.trim()),
        loading: busy,
      }}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <CalendarX size={16} weight="bold" />
          Dismiss this deadline?
        </span>
      }
      destroyOnClose
    >
      {cardTitle && (
        <InboxModalHint style={{ marginBottom: 12 }}>{cardTitle}</InboxModalHint>
      )}
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="deadline-dismiss-reason">
          Why is no calendar event needed?
        </InboxModalLabel>
        <Select
          id="deadline-dismiss-reason"
          value={reason}
          onChange={setReason}
          options={DISMISS_REASON_OPTIONS}
          style={{ width: "100%" }}
        />
      </InboxModalFieldGroup>
      <InboxModalFieldGroup>
        <InboxModalLabel htmlFor="deadline-dismiss-note">
          Note {reason === "other" ? "(required)" : "(optional)"}
        </InboxModalLabel>
        <Input.TextArea
          id="deadline-dismiss-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="e.g. paralegal already created the event on 7/14"
        />
        <InboxModalHint>
          The reason is recorded on the card so the nightly deadline guard
          treats it as an explicit decision, not a miss.
        </InboxModalHint>
      </InboxModalFieldGroup>
      {error && <InboxModalErrorBanner role="alert">{error}</InboxModalErrorBanner>}
    </Modal>
  );
};

DismissDeadlineModal.propTypes = {
  open: PropTypes.bool.isRequired,
  cardTitle: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

export default DismissDeadlineModal;
